import { useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import axios from "axios";
import { useAuth } from "@/lib/auth";
import { Sparkles, ClipboardCheck, AlertTriangle, MapPin } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const publicApi = axios.create({ baseURL: `${BACKEND_URL}/api` });

export default function ScanLandingPage() {
  const { areaId } = useParams();
  const { user, loading: authLoading } = useAuth();
  const nav = useNavigate();
  const [info, setInfo] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const { data } = await publicApi.get(`/public/areas/${areaId}`);
        setInfo(data);
      } catch (e) {
        setErr(e?.response?.data?.detail || "No se pudo cargar el área");
      }
    })();
  }, [areaId]);

  // Auto-redirect logged-in workers to the check-in if they want
  useEffect(() => {
    // Don't auto-redirect; let them choose
  }, [user, authLoading]);

  if (err) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6 bg-white">
        <div className="lx-card max-w-md w-full text-center">
          <h2 className="font-heading font-bold text-xl">Área no disponible</h2>
          <p className="text-sm text-lx-muted mt-2">{err}</p>
        </div>
      </div>
    );
  }

  if (!info) {
    return <div className="min-h-screen flex items-center justify-center"><div className="w-8 h-8 border-2 border-lx-primary border-t-transparent rounded-full animate-spin"/></div>;
  }

  return (
    <div className="min-h-screen bg-white flex flex-col">
      <div className="border-b border-lx-border bg-white px-5 py-3 flex items-center gap-2">
        <div className="w-7 h-7 rounded-md flex items-center justify-center" style={{ background: "#00AEEF" }}>
          <Sparkles size={16} color="#fff"/>
        </div>
        <div className="font-heading font-bold text-sm">Limpio<span className="text-lx-primary">Express</span></div>
      </div>

      <div className="flex-1 flex items-start justify-center p-5">
        <div className="w-full max-w-md mt-4">
          <div className="rounded-2xl p-6 text-white" style={{ background: "linear-gradient(135deg, #00AEEF, #008CBE)" }}>
            <div className="text-xs uppercase tracking-widest opacity-80 font-bold">Punto de control</div>
            <h1 className="font-heading font-bold text-3xl tracking-tight mt-1" data-testid="scan-area-name">{info.area.name}</h1>
            <div className="mt-3 space-y-1 text-sm opacity-95">
              {info.location && <div className="flex items-center gap-1.5"><MapPin size={14}/> {info.location.name}</div>}
              {info.client && <div className="flex items-center gap-1.5"><Sparkles size={14}/> {info.client.name}</div>}
            </div>
          </div>

          <div className="mt-5 space-y-3">
            <p className="text-sm text-lx-muted text-center">¿Qué quieres hacer?</p>
            <button
              onClick={() => nav(user ? `/check/${areaId}` : `/login`, { state: { from: `/check/${areaId}` } })}
              className="w-full p-5 rounded-2xl border-2 border-lx-border bg-white hover:border-lx-primary hover:bg-lx-primary/5 transition text-left"
              data-testid="scan-register-cleaning"
            >
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-xl flex items-center justify-center" style={{ background: "#00AEEF22", color: "#00AEEF" }}>
                  <ClipboardCheck size={24} strokeWidth={1.8}/>
                </div>
                <div>
                  <div className="font-heading font-bold text-lg">Registrar limpieza</div>
                  <div className="text-xs text-lx-muted">Personal de Limpio Express</div>
                </div>
              </div>
            </button>

            <Link
              to={`/report/${areaId}`}
              className="w-full p-5 rounded-2xl border-2 border-lx-border bg-white hover:border-amber-500 hover:bg-amber-50 transition text-left flex items-center gap-4 no-underline text-lx-text"
              data-testid="scan-report-issue"
            >
              <div className="w-12 h-12 rounded-xl flex items-center justify-center" style={{ background: "#F59E0B22", color: "#F59E0B" }}>
                <AlertTriangle size={24} strokeWidth={1.8}/>
              </div>
              <div>
                <div className="font-heading font-bold text-lg">Reportar un problema</div>
                <div className="text-xs text-lx-muted">Sin registro, anónimo</div>
              </div>
            </Link>
          </div>

          <p className="text-xs text-lx-muted text-center mt-6">
            Limpio Express · Control de actividades de limpieza por QR
          </p>
        </div>
      </div>
    </div>
  );
}
